import React from 'react';

import type { ChipProps, ChipShape, ChipSize, ChipVariant } from './Chip.types';

const SHAPE: Record<ChipShape, string> = {
  pill: 'rounded-full',
  square: 'rounded-md',
};

/**
 * Size → padding and type.
 *
 * `sm` and `md` are `FilterPill`'s, unchanged, because the calendar and the
 * exercise hero draw them. `lg` was `text-caption-md` with no consumer at all,
 * so it is now what the pill-shaped call sites actually use.
 *
 * None of them reach 44px on their own — `sm` renders at 22px. The floor is
 * applied separately as `pointer-coarse:min-h-11`, the same rule `Button` uses,
 * so the padding here stays the density these call sites were drawn at and only
 * a touch pointer pays for the target. See the base classes below.
 */
const SIZE: Record<ChipSize, string> = {
  sm: 'px-2 py-0.5 text-label-xs',
  md: 'px-2 py-1 text-caption-sm',
  lg: 'px-3 py-1 text-label-sm',
};

/**
 * Variant → the unselected treatment. Selected is the same sakura wash for all
 * of them; that agreement was the one thing every hand-rolled copy got right.
 */
const UNSELECTED: Record<ChipVariant, string> = {
  outline:
    'border-stroke-default text-neutral-400 hover:border-stroke-strong hover:text-neutral-200',
  filled: 'border-stroke-subtle bg-lift-1 text-neutral-300 hover:bg-lift-3',
};

const SELECTED = 'border-stroke-accent bg-sakura-wash text-primary-300';

/**
 * Chip - a token you can switch on and off.
 *
 * `FilterPill` already had this treatment, at `rounded-md`. Four places wanted
 * the round-pill geometry and rebuilt the whole control rather than ask for the
 * second radius — the questionnaire's multi-select, the report composer's
 * section toggles, the notifications "Unread only", and the weekday picker.
 * Every one of them wrote `border-stroke-accent bg-sakura-wash text-primary-300`
 * by hand for the selected state.
 *
 * The boundary between this and its neighbours, which mobile's `Chip` states
 * and web did not have anywhere:
 *
 * - **Chip** — interactive. You press it and it changes.
 * - **Badge** — prominent static state. It tells you something.
 * - **CardTag** — whisper-grade card metadata. It stays out of the way.
 *
 * `aria-pressed` is always set, so this is a toggle. A chip-shaped control that
 * navigates is a link, and a chip-shaped control that *creates* something — the
 * dashed "+ Other" and "+ Add measurement" affordances — is a button with no
 * pressed state, and neither belongs here.
 *
 * @example
 * <Chip label="Strength" selected={isOn} onClick={toggle} variant="filled" />
 * <Chip label="Unread only" selected={unreadOnly} onClick={toggle}
 *       size="lg" leading={<span className="h-1.5 w-1.5 rounded-full …" />} />
 */
export function Chip({
  label,
  selected = false,
  onClick,
  disabled = false,
  shape = 'pill',
  variant = 'outline',
  size = 'md',
  leading,
  trailing,
  'aria-label': ariaLabel,
  className = '',
  'data-testid': testId,
}: ChipProps): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={ariaLabel}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 border font-medium transition-colors duration-fast cursor-pointer pointer-coarse:min-h-11 focus:outline-none focus-visible:ring-state-focus ${
        SHAPE[shape]
      } ${SIZE[size]} ${selected ? SELECTED : UNSELECTED[variant]} ${
        disabled ? 'opacity-40 cursor-not-allowed' : ''
      } ${className}`
        .replace(/\s+/g, ' ')
        .trim()}
    >
      {leading && (
        <span className="flex-none" aria-hidden="true">
          {leading}
        </span>
      )}
      {label}
      {trailing}
    </button>
  );
}

export default Chip;
